const DATABASE_NAME = 'smartnotedoctor-recording-outbox';
const RETENTION_MS = 12 * 60 * 60 * 1000;
const SESSION_STORE = 'sessions';
const CHUNK_STORE = 'chunks';

// Audio stays here until the server acknowledges the complete recording.
// Callers must verify the authenticated user's ownership of a session before
// recovering its contents.
export class RecordingOutbox {
  constructor({ indexedDB = window.indexedDB, databaseName = DATABASE_NAME } = {}) {
    this.indexedDB = indexedDB;
    this.databaseName = databaseName;
    this.databasePromise = null;
  }

  async open() {
    if (!this.indexedDB) {
      throw new Error('Browser storage is unavailable. Recording cannot be saved safely.');
    }
    if (!this.databasePromise) {
      this.databasePromise = new Promise((resolve, reject) => {
        const request = this.indexedDB.open(this.databaseName, 1);
        request.onupgradeneeded = () => {
          const database = request.result;
          database.createObjectStore(SESSION_STORE, { keyPath: 'sessionId' });
          const chunks = database.createObjectStore(CHUNK_STORE, { keyPath: ['sessionId', 'chunkIndex'] });
          chunks.createIndex('sessionId', 'sessionId');
        };
        request.onerror = () => reject(request.error || new Error('Could not open recording storage.'));
        request.onblocked = () => reject(new Error('Recording storage is blocked by another browser tab.'));
        request.onsuccess = () => {
          const database = request.result;
          database.onversionchange = () => {
            database.close();
            this.databasePromise = null;
          };
          resolve(database);
        };
      });
      this.databasePromise.catch(() => { this.databasePromise = null; });
    }
    return this.databasePromise;
  }

  async transact(storeNames, mode, operation) {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeNames, mode);
      let result;
      let operationError;
      transaction.oncomplete = () => resolve(result);
      transaction.onabort = () => reject(operationError || transaction.error || new Error('Recording storage transaction was aborted.'));
      transaction.onerror = (event) => reject(operationError || transaction.error || event.target.error || new Error('Could not save recording data.'));
      const setResult = (value) => { result = value; };
      const fail = (error) => {
        operationError = error;
        transaction.abort();
      };
      const onSuccess = (request, callback) => {
        request.onsuccess = () => {
          try {
            callback(request.result);
          } catch (error) {
            fail(error);
          }
        };
      };
      try {
        operation(transaction, setResult, fail, onSuccess);
      } catch (error) {
        fail(error);
      }
    });
  }

  validateSession(session) {
    if (!session || typeof session.sessionId !== 'string' || !session.sessionId) {
      throw new Error('A recording session ID is required.');
    }
    if (!Number.isFinite(session.createdAt) || session.createdAt < 0 ||
        !Number.isFinite(session.actualDurationSeconds) || session.actualDurationSeconds < 0 ||
        (session.nextChunkIndex !== undefined && (!Number.isInteger(session.nextChunkIndex) || session.nextChunkIndex < 0)) ||
        (session.finalChunkIndex !== undefined && (!Number.isInteger(session.finalChunkIndex) || session.finalChunkIndex < -1))) {
      throw new Error('Invalid recording checkpoint.');
    }
  }

  mergeSession(previous, session) {
    const finalized = previous && previous.finalChunkIndex !== undefined;
    const checkpoint = {
      sessionId: session.sessionId,
      createdAt: previous ? previous.createdAt : session.createdAt,
      actualDurationSeconds: finalized
        ? previous.actualDurationSeconds
        : session.finalChunkIndex !== undefined
          ? session.actualDurationSeconds
          : Math.max(previous ? previous.actualDurationSeconds : 0, session.actualDurationSeconds),
    };
    if (finalized) {
      checkpoint.finalChunkIndex = previous.finalChunkIndex;
    } else if (session.finalChunkIndex !== undefined) {
      checkpoint.finalChunkIndex = session.finalChunkIndex;
    }
    if ((previous && previous.nextChunkIndex !== undefined) || session.nextChunkIndex !== undefined) {
      checkpoint.nextChunkIndex = checkpoint.finalChunkIndex !== undefined
        ? checkpoint.finalChunkIndex + 1
        : Math.max(previous ? previous.nextChunkIndex || 0 : 0, session.nextChunkIndex || 0);
    }
    return checkpoint;
  }

  async saveSession(session) {
    this.validateSession(session);
    return this.transact([SESSION_STORE], 'readwrite', (transaction, setResult, fail, onSuccess) => {
      const store = transaction.objectStore(SESSION_STORE);
      const request = store.get(session.sessionId);
      onSuccess(request, (previous) => {
        const checkpoint = this.mergeSession(previous, session);
        store.put(checkpoint);
        setResult(checkpoint);
      });
    });
  }

  async saveChunk(sessionId, chunkIndex, blob, checkpoint) {
    if (typeof sessionId !== 'string' || !sessionId || !Number.isInteger(chunkIndex) || chunkIndex < 0 ||
        !blob || typeof blob.size !== 'number' || typeof blob.slice !== 'function') {
      throw new Error('Invalid recording chunk.');
    }
    if (checkpoint) {
      this.validateSession(checkpoint);
      if (checkpoint.sessionId !== sessionId) throw new Error('Recording checkpoint belongs to another session.');
    }
    return this.transact([SESSION_STORE, CHUNK_STORE], 'readwrite', (transaction, setResult, fail, onSuccess) => {
      const request = transaction.objectStore(SESSION_STORE).get(sessionId);
      onSuccess(request, (session) => {
        if (!session && !checkpoint) {
          fail(new Error('The recording session must be saved before its audio.'));
          return;
        }
        const metadata = this.mergeSession(session, {
          ...(checkpoint || session),
          nextChunkIndex: Math.max(checkpoint ? checkpoint.nextChunkIndex || 0 : 0, chunkIndex + 1),
        });
        transaction.objectStore(SESSION_STORE).put(metadata);
        transaction.objectStore(CHUNK_STORE).put({ sessionId, chunkIndex, blob });
        setResult(metadata);
      });
    });
  }

  async listSessions() {
    return this.transact([SESSION_STORE], 'readonly', (transaction, setResult, fail, onSuccess) => {
      const request = transaction.objectStore(SESSION_STORE).getAll();
      onSuccess(request, (sessions) => setResult(sessions.sort((a, b) => a.createdAt - b.createdAt)));
    });
  }

  async listChunks(sessionId) {
    return this.transact([CHUNK_STORE], 'readonly', (transaction, setResult, fail, onSuccess) => {
      const request = transaction.objectStore(CHUNK_STORE).index('sessionId').getAll(sessionId);
      onSuccess(request, (chunks) => setResult(chunks.sort((a, b) => a.chunkIndex - b.chunkIndex)));
    });
  }

  async getChunk(sessionId, chunkIndex) {
    return this.transact([CHUNK_STORE], 'readonly', (transaction, setResult, fail, onSuccess) => {
      const request = transaction.objectStore(CHUNK_STORE).get([sessionId, chunkIndex]);
      onSuccess(request, setResult);
    });
  }

  deleteSessionInTransaction(transaction, sessionId, onSuccess) {
    transaction.objectStore(SESSION_STORE).delete(sessionId);
    const chunks = transaction.objectStore(CHUNK_STORE);
    const request = chunks.index('sessionId').openKeyCursor(sessionId);
    onSuccess(request, (cursor) => {
      if (cursor) {
        chunks.delete(cursor.primaryKey);
        cursor.continue();
      }
    });
  }

  async deleteSession(sessionId) {
    return this.transact([SESSION_STORE, CHUNK_STORE], 'readwrite', (transaction, setResult, fail, onSuccess) => {
      this.deleteSessionInTransaction(transaction, sessionId, onSuccess);
    });
  }

  async pruneExpired(now = Date.now()) {
    return this.transact([SESSION_STORE, CHUNK_STORE], 'readwrite', (transaction, setResult, fail, onSuccess) => {
      const request = transaction.objectStore(SESSION_STORE).getAll();
      onSuccess(request, (sessions) => {
        const expired = sessions.filter((session) => now - session.createdAt >= RETENTION_MS);
        expired.forEach((session) => this.deleteSessionInTransaction(transaction, session.sessionId, onSuccess));
        setResult(expired.length);
      });
    });
  }
}
